import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Scheduler,
  WeekView,
  MonthView,
  DayView,
  Appointments,
  AppointmentTooltip,
  Toolbar,
  DateNavigator,
  ViewSwitcher,
  TodayButton,
  DragDropProvider,
} from "@devexpress/dx-react-scheduler-material-ui";
import { ViewState, EditingState, IntegratedEditing } from "@devexpress/dx-react-scheduler";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";

import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import ContentBox from "../../components/ui/ContentBox";

/**
 * UX 13 — Drag-and-drop weekly template editor (mode = "templates").
 * UX 14 — Drag-and-drop visual term planner       (mode = "term").
 *
 * Templates mode loads CourtWeeklySchedule rows for the selected term/court
 * and PATCHes /courtWeeklySchedules/{id} on drop.
 * Term mode loads concrete CourtSchedules across the whole term and PATCHes
 * /courtSchedules/{id} on drop. Both surface backend conflict warnings via
 * toast (QW6).
 *
 * Mounted at `/admin/schedules/template-editor`.
 */
function WeeklyTemplateEditor() {
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const headers = useMemo(
    () => ({ "content-type": "application/json", authorization: `Bearer ${access_token}` }),
    [access_token]
  );

  // ──────────────────────────────────────────────────────────
  // pickers + mode
  // ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState("templates"); // "templates" | "term"
  const [terms, setTerms] = useState([]);
  const [courts, setCourts] = useState([]);
  const [term, setTerm] = useState(null);
  const [court, setCourt] = useState(null);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [currentViewName, setCurrentViewName] = useState("Week");
  const [currentDate, setCurrentDate] = useState(new Date());

  // ──────────────────────────────────────────────────────────
  // data + ui state
  // ──────────────────────────────────────────────────────────
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const savingIdsRef = useRef(new Set());
  const [reload, setReload] = useState(false);

  useEffect(() => { document.title = "Sportec — Drag-and-drop schedule editor"; }, []);

  // initial pickers
  useEffect(() => {
    Api.get(Urls.GET_TERMS_LIST + "?perPage=200", { headers })
      .then((res) => setTerms(res?.data?.data || []))
      .catch(() => {});
    Api.get(Urls.GET_COURTS_LIST + "?perPage=200", { headers })
      .then((res) => setCourts(res?.data?.data || []))
      .catch(() => {});
  }, [headers]);

  // when term picked → jump calendar to its start date
  useEffect(() => {
    if (term?.start_date) {
      const d = new Date(term.start_date);
      if (!isNaN(d.getTime())) setCurrentDate(d);
    }
  }, [term]);

  // when switching mode: month works for term, week works for both
  useEffect(() => {
    if (mode === "templates" && currentViewName === "Month") setCurrentViewName("Week");
  }, [mode, currentViewName]);

  // ──────────────────────────────────────────────────────────
  // colour helpers
  // ──────────────────────────────────────────────────────────
  const SESSION_TYPE_FALLBACK = {
    PRIVATE_LESSON: { start: "#7367F0", end: "#5145D8" },
    COURT_BOOKING:  { start: "#FF9F43", end: "#E78224" },
    PROGRAM:        { start: "#28C76F", end: "#1F9D55" },
  };

  const colorsFor = (row) => {
    if (row?.cancelled_at) return { start: "#9CA3AF", end: "#6B7280" };
    // BTA API stores program colors as raw hex ("4a90e2") without leading '#'.
    // CSS rejects gradient colors that lack the prefix — normalise here.
    const norm = (h) => {
      if (!h) return null;
      const s = String(h).trim();
      if (s.startsWith("#") || s.startsWith("rgb") || s.startsWith("hsl")) return s;
      return `#${s.replace(/^#/, "")}`;
    };
    const s = norm(row?.program_color_start);
    const e = norm(row?.program_color_end);
    if (s && e) return { start: s, end: e };
    return SESSION_TYPE_FALLBACK[row?.session_type] || SESSION_TYPE_FALLBACK.PROGRAM;
  };

  // ──────────────────────────────────────────────────────────
  // shape adapters: weekly templates → scheduler events
  // ──────────────────────────────────────────────────────────
  const dayMapping = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  const indexToDay = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  const referenceSunday = useMemo(() => {
    const d = new Date(currentDate);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay());
    return d;
  }, [currentDate]);

  const buildTemplateEvents = useCallback((rows) => {
    return (rows || []).map((row) => {
      const idx = dayMapping[String(row.day_name).toLowerCase()] ?? 1;
      const date = new Date(referenceSunday);
      date.setDate(referenceSunday.getDate() + idx);
      const [sh, sm] = String(row.start_time || "00:00:00").split(":");
      const [eh, em] = String(row.end_time || "00:00:00").split(":");
      const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), +sh, +sm);
      const endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), +eh, +em);
      return {
        id: row.id,
        title: row.program_name || row.session_type || "Session",
        startDate,
        endDate,
        rawRow: { ...row, kind: "template" },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceSunday]);

  // shape adapter: concrete schedules → scheduler events (UX 14)
  const buildConcreteEvents = (rows) => {
    return (rows || []).map((row) => ({
      id: row.id,
      title: row.program_name || row.session_type || "Session",
      startDate: new Date(row.start_datetime || row.startDate),
      endDate: new Date(row.end_datetime || row.endDate),
      rawRow: { ...row, kind: "concrete" },
    }));
  };

  // ──────────────────────────────────────────────────────────
  // data load
  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!term?.id) {
      setSchedule([]);
      return;
    }
    setLoading(true);

    if (mode === "templates") {
      Api.get(Urls.GET_WEEKLY_SCHEDULE + term.id + "/courtWeeklySchedules", {
        headers,
        params: { ...(court?.id && { courtId: court.id }) },
      })
        .then((res) => {
          let rows = res?.data?.data || [];
          if (!includeCancelled) rows = rows.filter((r) => !r.cancelled_at);
          setSchedule(buildTemplateEvents(rows));
        })
        .catch((err) => toast.error(err?.response?.data?.message || "Failed to load templates"))
        .finally(() => setLoading(false));
    } else {
      Api.get(Urls.GET_GLOBAL_SCHEDULE + term.id + "/courtSchedules", {
        headers,
        params: {
          ...(court?.id && { courtId: court.id }),
          startDatetime: new Date(term.start_date),
          endDatetime: new Date(term.end_date),
        },
      })
        .then((res) => {
          let rows = res?.data?.data || [];
          if (!includeCancelled) rows = rows.filter((r) => !r.cancelled_at);
          setSchedule(buildConcreteEvents(rows));
        })
        .catch((err) => toast.error(err?.response?.data?.message || "Failed to load sessions"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, term, court, includeCancelled, reload, headers]);

  // QW6
  const surfaceWarnings = (res) => {
    const w = res?.data?.data?.warnings || res?.data?.warnings;
    if (!w) return;
    const c = (w.court_conflicts || []).length;
    const k = (w.coach_conflicts || []).length;
    if (c || k) {
      toast.warn(`Saved with ${c} court overlap${c === 1 ? "" : "s"} and ${k} coach overlap${k === 1 ? "" : "s"}`, {
        autoClose: 6000,
      });
    }
  };

  // ──────────────────────────────────────────────────────────
  // commit (drag/resize)
  // ──────────────────────────────────────────────────────────
  const handleCommitChanges = useCallback(({ changed }) => {
    if (!changed) return;

    Object.entries(changed).forEach(([id, patch]) => {
      const original = schedule.find((s) => String(s.id) === String(id));
      if (!original) return;
      const newStart = patch.startDate ? new Date(patch.startDate) : original.startDate;
      const newEnd = patch.endDate ? new Date(patch.endDate) : original.endDate;

      // UX 14 — guard term boundaries (only meaningful for concrete sessions;
      // weekly templates are inherently term-bound by their FK).
      if (original.rawRow?.kind === "concrete" && term) {
        const ts = new Date(term.start_date); ts.setHours(0, 0, 0, 0);
        const te = new Date(term.end_date); te.setHours(23, 59, 59, 999);
        if (newStart < ts || newEnd > te) {
          toast.error("Cannot move outside the term date range");
          setReload((r) => !r);
          return;
        }
      }

      // optimistic update
      setSchedule((prev) =>
        prev.map((s) => (String(s.id) === String(id) ? { ...s, startDate: newStart, endDate: newEnd } : s))
      );
      savingIdsRef.current.add(String(id));

      let req;
      if (original.rawRow?.kind === "template") {
        // UX 13 — PATCH /courtWeeklySchedules/{id}
        const day_name = indexToDay[newStart.getDay()];
        const start_time = moment(newStart).format("HH:mm:ss");
        const end_time = moment(newEnd).format("HH:mm:ss");
        req = Api.patch(
          Urls.UPDATE_COURT_WEEKLY_SCHEDULE + id,
          {
            court_weekly_schedules_id: id,
            court_id: original.rawRow?.court_id || court?.id,
            term_id: term?.id,
            day_name,
            start_time,
            end_time,
          },
          { headers }
        );
      } else {
        // UX 14 — PATCH /courtSchedules/{id}
        req = Api.patch(
          Urls.UPDATE_COURT_SCHEDULE + id,
          {
            start_datetime: moment(newStart).format("YYYY-MM-DD HH:mm:ss"),
            end_datetime: moment(newEnd).format("YYYY-MM-DD HH:mm:ss"),
          },
          { headers }
        );
      }

      req
        .then((res) => {
          toast.success("Session moved");
          surfaceWarnings(res);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || "Move failed — reverting");
        })
        .finally(() => {
          savingIdsRef.current.delete(String(id));
          setReload((r) => !r); // re-pull canonical
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, term, court, headers, indexToDay]);

  // ──────────────────────────────────────────────────────────
  // appointment renderer (gradient bg from program colors + coach + levels)
  // ──────────────────────────────────────────────────────────
  const Appointment = ({ children, style, data, ...rest }) => {
    const c = colorsFor(data?.rawRow);
    const isSaving = savingIdsRef.current.has(String(data?.id));
    return (
      <Appointments.Appointment
        {...rest}
        data={data}
        style={{
          ...style,
          background: `linear-gradient(135deg, ${c.start}, ${c.end})`,
          borderRadius: "6px",
          opacity: isSaving ? 0.4 : data?.rawRow?.cancelled_at ? 0.6 : 1,
          color: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
          border: data?.rawRow?.cancelled_at ? "1px dashed #fff" : "none",
        }}
      >
        {children}
      </Appointments.Appointment>
    );
  };

  const AppointmentContent = ({ data, ...rest }) => {
    const coach = data?.rawRow?.coach_first_name
      ? `${data.rawRow.coach_first_name} ${data.rawRow.coach_last_name || ""}`.trim()
      : null;
    const levels = data?.rawRow?.levels || [];
    const tStart = moment(data?.startDate).format("HH:mm");
    const tEnd = moment(data?.endDate).format("HH:mm");

    return (
      <Appointments.AppointmentContent {...rest} data={data}>
        <Box sx={{ p: "4px 6px", color: "#fff", overflow: "hidden", height: "100%" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {data?.title}
          </Typography>
          {coach && (
            <Typography sx={{ fontSize: 10.5, opacity: 0.95, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {coach}
            </Typography>
          )}
          <Typography sx={{ fontSize: 10, opacity: 0.85 }}>
            {tStart} – {tEnd}
          </Typography>
          {levels.length > 0 && (
            <Stack direction="row" spacing={0.4} sx={{ mt: 0.3, flexWrap: "wrap", gap: 0.3 }}>
              {levels.slice(0, 3).map((l) => (
                <Chip
                  key={l.id}
                  label={l.name}
                  size="small"
                  sx={{
                    height: 14,
                    fontSize: 9,
                    bgcolor: "rgba(255,255,255,0.25)",
                    color: "#fff",
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Appointments.AppointmentContent>
    );
  };

  // tooltip body — richer detail
  const TooltipContent = ({ appointmentData, ...rest }) => {
    const r = appointmentData?.rawRow || {};
    const coach = r.coach_first_name ? `${r.coach_first_name} ${r.coach_last_name || ""}`.trim() : "—";
    const levels = (r.levels || []).map((l) => l.name).join(", ");
    return (
      <AppointmentTooltip.Content {...rest} appointmentData={appointmentData}>
        <Box sx={{ p: 2, fontSize: 13 }}>
          <Typography fontSize={14} fontWeight={600} mb={0.5}>{appointmentData?.title}</Typography>
          <Box>Coach: {coach}</Box>
          {levels && <Box>Levels: {levels}</Box>}
          {r.duration && <Box>Duration: {r.duration} min</Box>}
          {r.cancelled_at && <Box sx={{ color: "warning.main" }}>Cancelled at {r.cancelled_at}</Box>}
          {r.kind === "template" && <Box sx={{ mt: 1, opacity: 0.7 }}>Weekly template #{r.id}</Box>}
          {r.kind === "concrete" && <Box sx={{ mt: 1, opacity: 0.7 }}>Session #{r.id}</Box>}
        </Box>
      </AppointmentTooltip.Content>
    );
  };

  // disable drag for cancelled events
  const allowDrag = (appointmentData) => !appointmentData?.rawRow?.cancelled_at;

  // Compact appointment for MonthView — month cells are tight, render as a thin
  // colored bar with the program name + time only.
  const MonthAppointment = ({ children, style, data, ...rest }) => {
    const c = colorsFor(data?.rawRow);
    const isSaving = savingIdsRef.current.has(String(data?.id));
    return (
      <Appointments.Appointment
        {...rest}
        data={data}
        style={{
          ...style,
          background: `linear-gradient(135deg, ${c.start}, ${c.end})`,
          borderRadius: "3px",
          opacity: isSaving ? 0.4 : data?.rawRow?.cancelled_at ? 0.6 : 1,
          color: "#fff",
          height: 16,
          marginTop: 1,
          marginBottom: 1,
          minHeight: 16,
        }}
      >
        <Box sx={{ px: 0.6, fontSize: 10, lineHeight: "16px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {moment(data?.startDate).format("HH:mm")} {data?.title}
        </Box>
      </Appointments.Appointment>
    );
  };

  // Auto-scroll the scheduler container while dragging (DevExpress doesn't do
  // this natively). Listens at the wrapper level so it works for any view.
  const wrapperRef = useRef(null);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    let scrollEl = null;
    let dragging = false;
    let raf = null;
    let pointerY = 0;

    const findScrollEl = () => {
      // DevExpress wraps the time-grid in a div with overflow-y:auto. The
      // closest reliable selector is a child div with significant scrollHeight.
      const candidates = wrapper.querySelectorAll("div");
      for (const el of candidates) {
        if (el.scrollHeight - el.clientHeight > 40 && getComputedStyle(el).overflowY !== "visible") {
          return el;
        }
      }
      return null;
    };

    const tick = () => {
      raf = null;
      if (!dragging || !scrollEl) return;
      const rect = scrollEl.getBoundingClientRect();
      const edge = 60;
      let dy = 0;
      if (pointerY < rect.top + edge) dy = -Math.max(4, (rect.top + edge - pointerY) / 4);
      else if (pointerY > rect.bottom - edge) dy = Math.max(4, (pointerY - (rect.bottom - edge)) / 4);
      if (dy) scrollEl.scrollTop += dy;
      raf = requestAnimationFrame(tick);
    };

    const onMouseDown = (e) => {
      if (!e.target.closest(".Appointment-appointment, [class*='Appointment-appointment']")) return;
      scrollEl = findScrollEl();
      dragging = true;
      pointerY = e.clientY;
      raf = requestAnimationFrame(tick);
    };
    const onMouseMove = (e) => {
      if (dragging) pointerY = e.clientY;
    };
    const onMouseUp = () => {
      dragging = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };

    wrapper.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      wrapper.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [schedule, currentViewName]);

  // ──────────────────────────────────────────────────────────
  // render
  // ──────────────────────────────────────────────────────────
  const minDate = term ? new Date(term.start_date) : undefined;
  const maxDate = term ? new Date(term.end_date) : undefined;

  // Auto-fit start/end day-hour to real data so users don't have to scroll
  // vertically (which conflicts with DevExpress drag-and-drop).
  const { startDayHour, endDayHour } = useMemo(() => {
    if (!schedule.length) return { startDayHour: 8, endDayHour: 22 };
    let minH = 23;
    let maxH = 0;
    schedule.forEach((s) => {
      const sh = s.startDate.getHours() + s.startDate.getMinutes() / 60;
      const eh = s.endDate.getHours() + s.endDate.getMinutes() / 60;
      if (sh < minH) minH = sh;
      if (eh > maxH) maxH = eh;
    });
    return {
      startDayHour: Math.max(0, Math.floor(minH) - 1),
      endDayHour: Math.min(24, Math.ceil(maxH) + 1),
    };
  }, [schedule]);

  return (
    <Box padding="24px" display="flex" flexDirection="column" gap="16px">
      <ToastContainer />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="12px">
        <Box>
          <Typography fontSize="22px" fontWeight="600" color="#4B465C">
            Drag-and-drop schedule editor
          </Typography>
          <Typography fontSize="13px" color="#7B7A86">
            Drag sessions to move them. Resize the bottom edge to extend/shorten. Conflicts are reported but never blocked.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          color="primary"
        >
          <ToggleButton value="templates">UX 13 · Weekly templates</ToggleButton>
          <ToggleButton value="term">UX 14 · Term planner</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ContentBox>
        <Box display="flex" gap="16px" alignItems="center" flexWrap="wrap">
          <Autocomplete
            sx={{ minWidth: 260 }}
            options={terms}
            value={term}
            onChange={(_, v) => setTerm(v)}
            getOptionLabel={(t) => t?.name || ""}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => <TextField {...params} label="Term" size="small" />}
          />
          <Autocomplete
            sx={{ minWidth: 240 }}
            options={courts}
            value={court}
            onChange={(_, v) => setCourt(v)}
            getOptionLabel={(c) => c?.name || ""}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => <TextField {...params} label={mode === "templates" ? "Court (optional)" : "Court (recommended)"} size="small" />}
          />
          <FormControlLabel
            control={<Switch checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} />}
            label="Include cancelled"
          />
          <Button variant="primary_outlined" onClick={() => setReload((r) => !r)}>Refresh</Button>
          {term && (
            <Tooltip title="Term date range">
              <Chip
                size="small"
                label={`${moment(term.start_date).format("DD MMM")} – ${moment(term.end_date).format("DD MMM YYYY")}`}
              />
            </Tooltip>
          )}
        </Box>
      </ContentBox>

      <ContentBox>
        {!term && <Alert severity="info">Pick a term to start editing.</Alert>}
        {term && loading && (
          <Box display="flex" justifyContent="center" padding="40px"><CircularProgress /></Box>
        )}
        {term && !loading && schedule.length === 0 && (
          <Alert severity="warning">
            {mode === "templates" ? "No weekly templates" : "No concrete sessions"} for this {court ? "court" : "term"}.
          </Alert>
        )}
        {term && !loading && schedule.length > 0 && (
          <Box ref={wrapperRef} sx={{ position: "relative", "& .MuiToolbar-root": { bgcolor: "transparent" } }}>
            <Scheduler data={schedule} height={Math.max(560, Math.min(900, (endDayHour - startDayHour) * 60 + 120))} firstDayOfWeek={0}>
              <ViewState
                currentDate={currentDate}
                onCurrentDateChange={(d) => {
                  if (mode === "term" && minDate && maxDate) {
                    const c = new Date(d);
                    if (c < minDate) return setCurrentDate(minDate);
                    if (c > maxDate) return setCurrentDate(maxDate);
                  }
                  setCurrentDate(d);
                }}
                currentViewName={currentViewName}
                onCurrentViewNameChange={setCurrentViewName}
              />
              <EditingState onCommitChanges={handleCommitChanges} />
              <IntegratedEditing />

              <DayView startDayHour={startDayHour} endDayHour={endDayHour} cellDuration={30} />
              <WeekView startDayHour={startDayHour} endDayHour={endDayHour} cellDuration={30} />
              {mode === "term" && <MonthView />}

              <Appointments
                appointmentComponent={currentViewName === "Month" ? MonthAppointment : Appointment}
                appointmentContentComponent={currentViewName === "Month" ? undefined : AppointmentContent}
              />
              <AppointmentTooltip showCloseButton contentComponent={TooltipContent} />

              <Toolbar />
              <DateNavigator />
              <TodayButton />
              <ViewSwitcher />
              <DragDropProvider allowDrag={allowDrag} allowResize={allowDrag} />
            </Scheduler>
          </Box>
        )}
      </ContentBox>
    </Box>
  );
}

export default WeeklyTemplateEditor;
